import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { deleteR2File, renameR2File } from 'src/actions/watcher/r2FilesActions';
import { R2ObjectInterface } from 'src/store/watcher/r2FilesSlice';
import { formattedDate } from 'src/helpers/DateHelpers';
import Table from 'src/componets/Table';
import { Input, message } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { ButtonDestroy } from 'src/componets/Buttons';
import { CopyOutlined, EditOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import Wrapper from '../../../../componets/Wrapper';
import ListViewButtons from '../../../../componets/ListViewButtons';
import usePermissions from 'src/hooks/usePermissions';

function formatSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
}

const FileList = () => {
  const dispatch = useAppDispatch();
  const objects = useAppSelector(state => state.watcher.r2Files.objects);
  const activeBucketId = useAppSelector(state => state.watcher.r2Files.activeBucketId);
  const { write_r2_files } = usePermissions();
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');

  function startRename(record: R2ObjectInterface) {
    setEditingKey(record.key);
    setEditingName(record.filename);
  }

  function cancelRename() {
    setEditingKey(null);
    setEditingName('');
  }

  async function confirmRename(oldKey: string) {
    const trimmed = editingName.trim();
    if (!trimmed) {
      message.warning('Filename cannot be empty');
      return;
    }

    const resultAction = await dispatch(renameR2File({ key: oldKey, filename: trimmed, bucket_id: activeBucketId || undefined }));
    if (renameR2File.fulfilled.match(resultAction)) {
      message.success('File renamed');
      setEditingKey(null);
      setEditingName('');
    } else {
      message.error('Failed to rename file');
    }
  }

  async function handleDelete(key: string) {
    const resultAction = await dispatch(deleteR2File({ key, bucket_id: activeBucketId || undefined }));
    if (deleteR2File.fulfilled.match(resultAction)) {
      message.success('File deleted');
    } else {
      message.error('Failed to delete file');
    }
  }

  function handleCopyUrl(url: string) {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url);
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = url;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    message.success('URL copied');
  }

  const columns: ColumnsType<R2ObjectInterface> = [
    {
      key: 'filename',
      title: 'Name',
      ellipsis: true,
      render: (_: any, record: R2ObjectInterface) => {
        if (editingKey === record.key) {
          return (
            <Input
              size="small"
              value={editingName}
              onChange={e => setEditingName(e.target.value)}
              onPressEnter={() => confirmRename(record.key)}
              onKeyDown={e => e.key === 'Escape' && cancelRename()}
              autoFocus
              style={{ width: 300 }}
            />
          );
        }
        return record.filename;
      },
    },
    {
      key: 'size',
      title: 'Size',
      render: (_, { size }) => <small>{formatSize(size)}</small>,
      width: 100,
    },
    {
      key: 'last_modified',
      title: 'Modified',
      render: (_, { last_modified }) => <small>{formattedDate(last_modified).dateTime}</small>,
      width: 180,
    },
    {
      key: 'actions',
      title: 'Actions',
      width: 220,
      render: (_: any, record: R2ObjectInterface) => {
        if (editingKey === record.key) {
          return (
            <div style={{ display: 'flex', gap: 8 }}>
              <CheckOutlined
                style={{ color: '#52c41a', cursor: 'pointer' }}
                onClick={() => confirmRename(record.key)}
              />
              <CloseOutlined
                style={{ cursor: 'pointer' }}
                onClick={cancelRename}
              />
            </div>
          );
        }
        return (
          <div style={{ display: 'flex', gap: 8 }}>
            {record.url && (
              <span className='v-link2' onClick={() => handleCopyUrl(record.url!)} style={{ cursor: 'pointer' }}>
                <CopyOutlined /> URL
              </span>
            )}
            {write_r2_files && (
              <>
                <span style={{ cursor: 'pointer' }} className='v-link2' onClick={() => startRename(record)}>
                  <EditOutlined /> Rename
                </span>
                <ButtonDestroy onClick={() => handleDelete(record.key)} title='Delete' />
              </>
            )}
          </div>
        );
      },
    },
  ];

  if (!objects.length) return null;

  return (
    <>
      <Wrapper type='type-1' border className='v-mb-10'>
        <ListViewButtons align='right' defaultType='list' />
      </Wrapper>

      <Table
        rowKey='key'
        columns={columns}
        dataSource={objects}
      />
    </>
  );
};

export default FileList;
